import React, { useEffect, useState } from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Section } from '../fx/AccentContext';
import { MaskedLines } from '../fx/MaskedLines';
import LivingImage from '../fx/LivingImage';
import { SectionLabel, PADX } from './ui';
import { EASE_EXPO, EASE_QUINT } from '../fx/ease';
import { TrooperWordmark } from '../brand/TrooperLogo';
import { businesses } from '../../data/businesses';
import { descriptor } from '../../data/identity';

// 各事業の英字見出し（Trooper はロゴで見せる）
const DISPLAY = {
  trooper: null,
  enablement: 'AI ENABLEMENT',
  consulting: 'AI CONSULTING',
};

const BusinessesSection = () => {
  const [active, setActive] = useState(0);
  const [fine, setFine] = useState(true);
  const reduce = useReducedMotion();

  useEffect(() => {
    setFine(window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }, []);

  const onRowClick = (e, i) => {
    // タッチ環境: 1タップ目は展開、2タップ目で遷移
    if (!fine && active !== i) {
      e.preventDefault();
      setActive(i);
    }
  };

  return (
    <Section id="business" index={1} accent="blue" className="border-t border-line">
      <div className={`${PADX} py-24 md:py-32`}>
        <div className="grid grid-cols-12 gap-y-14 lg:gap-x-10">
          {/* 左: 見出し + 3事業のアコーディオン */}
          <div className="col-span-12 lg:col-span-6">
            <SectionLabel no="02">What We Do</SectionLabel>
            <MaskedLines
              as="h2"
              per="char"
              lines={['WHAT', 'WE DO']}
              className="display-type mt-8 text-[11.5vw] text-ink sm:text-6xl lg:text-[4.6vw] 2xl:text-[76px]"
              stagger={0.12}
            />
            <MaskedLines
              lines={[['AIをつくる力と、企業の仕事に届ける力。', 'その両方を持つ。']]}
              className="mt-6 text-[15px] font-bold leading-relaxed tracking-display-ja text-ink md:text-[17px]"
              delay={0.25}
            />
            <motion.p
              className="mt-4 max-w-[34em] text-[14px] leading-[1.9] text-gray-700"
              initial={reduce ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.7, ease: EASE_EXPO, delay: 0.3 }}
            >
              {descriptor}
            </motion.p>

            <div className="mt-12 border-t border-line md:mt-14">
              {businesses.map((b, i) => {
                const isActive = active === i;
                const dimmed = fine && !isActive;
                return (
                  // 各事業のLPは新しいタブで開く（2026-10-17 代表指示）
                  <a
                    key={b.id}
                    href={b.lp}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => onRowClick(e, i)}
                    onPointerEnter={(e) => {
                      if (e.pointerType === 'mouse') setActive(i);
                    }}
                    onFocus={() => setActive(i)}
                    data-cursor="VIEW"
                    className={`group block border-b border-line transition-opacity duration-500 ${
                      dimmed ? 'opacity-50 hover:opacity-100' : 'opacity-100'
                    }`}
                  >
                    <div className="flex items-center gap-5 py-6 md:gap-7">
                      <span
                        className={`text-[12px] font-semibold tracking-label tnum transition-colors duration-300 ${
                          isActive ? 'text-live' : 'text-gray-500'
                        }`}
                      >
                        {b.num}
                      </span>
                      <span
                        className={`flex min-w-0 flex-col gap-1.5 transition-transform duration-500 ease-expo ${
                          isActive ? 'translate-x-1' : ''
                        }`}
                      >
                        <span className="text-[10px] font-semibold uppercase tracking-label text-gray-500">
                          {b.en}
                        </span>
                        {DISPLAY[b.id] ? (
                          <span className="display-type text-[26px] leading-none text-ink md:text-4xl">
                            {DISPLAY[b.id]}
                          </span>
                        ) : (
                          <TrooperWordmark className="h-[26px] w-auto text-ink md:h-9" />
                        )}
                        <span className="text-xs font-medium text-gray-600 md:text-[13px]">{b.name}</span>
                      </span>
                      <ArrowRight
                        size={18}
                        className={`ml-auto shrink-0 transition-all duration-500 ease-expo ${
                          isActive ? 'translate-x-0 text-live opacity-100' : '-translate-x-2 text-ink opacity-40'
                        }`}
                        aria-hidden="true"
                      />
                    </div>

                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          key="body"
                          initial={reduce ? false : { height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={reduce ? undefined : { height: 0, opacity: 0 }}
                          transition={{ duration: 0.55, ease: EASE_QUINT }}
                          className="overflow-hidden"
                        >
                          <div className="pb-8 pl-10 pr-4 md:pl-12">
                            <p className="text-[15px] font-bold tracking-display-ja text-ink md:text-base">
                              {b.headline}
                            </p>
                            <p className="mt-3 max-w-[36em] text-sm leading-[1.9] text-gray-700 md:text-[15px]">
                              {b.desc}
                            </p>
                            <span className="mt-5 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-label text-live">
                              View {b.en}
                              <ArrowUpRight
                                size={14}
                                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                aria-hidden="true"
                              />
                            </span>
                            {/* モバイル: 行内に画像 */}
                            <div className="mt-6 lg:hidden">
                              <LivingImage
                                src={b.img}
                                alt=""
                                mode="lit"
                                curtain={false}
                                sweep={false}
                                className="aspect-[16/9] w-full"
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </a>
                );
              })}
            </div>

          </div>

          {/* 右: アクティブな事業の建築イメージ（クリップワイプで入替え） */}
          <div className="relative col-span-12 hidden lg:col-span-6 lg:block">
            <div className="sticky top-[104px] ml-auto aspect-[4/3] w-full max-w-[720px] overflow-hidden xl:mr-[-56px] 2xl:mr-0">
              <AnimatePresence initial={false}>
                <motion.div
                  key={businesses[active].img}
                  className="absolute inset-0"
                  initial={reduce ? { opacity: 0 } : { clipPath: 'inset(0 0 0 100%)' }}
                  animate={reduce ? { opacity: 1 } : { clipPath: 'inset(0 0 0 0%)' }}
                  exit={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: EASE_QUINT }}
                >
                  <LivingImage
                    src={businesses[active].img}
                    alt={`${businesses[active].name}のブランドイメージ`}
                    mode="lit"
                    radius={260}
                    curtain={false}
                    sweep
                    className="h-full w-full"
                  />
                </motion.div>
              </AnimatePresence>
              <motion.span
                aria-hidden="true"
                className="absolute bottom-0 left-0 z-10 h-[3px] w-full origin-left bg-live"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, margin: '-10%' }}
                transition={{ duration: 1.1, ease: EASE_EXPO, delay: 0.3 }}
              />
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default BusinessesSection;
